import React, { useCallback, useMemo, useRef } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { useRecents } from '../../../components/sidebar/active-domain-selector/hooks/use-recents'
import { AppActionContext } from '../../../enums/app-action-context'
import { PlatformUserDomainRecord } from '../../../models/domain/platform-user-domain-record'
import { AppService } from '../../../services/app'
import { AppState } from '../../../stores/app'

/** The active-domain selector's own default for how many recently opened domains it remembers. */
const RECENT_DOMAINS_LIMIT = 3

/**
 * Opening something that lives inside a domain from the organization-context dashboard. Only a domain the user
 * can switch to can be opened, because the active-domain context cannot be set for any other; callers show a
 * link for those and plain text for the rest. Opening switches the active domain as the active-domain selector
 * does, remembers it among the recently opened domains, and then routes to `path`. A modified click opens the
 * path in a new window without switching.
 */
export const useOpenInDomainContext = () => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    // Recently opened domains are stored at the user level, not per organization, as the selector does.
    const userLevelPreferences = useUserPreferences(appState.currentUser?.id)
    // Keyed on the domain ids, which are read on every render so an observing component re-renders when the list
    // changes, including when it is changed in place.
    const switchableDomains = appState.currentUserDomains ?? []
    const switchableDomainIdsKey = switchableDomains.map((record) => record.id).join(',')
    const switchableDomainsById = useMemo(
        () => new Map<number, PlatformUserDomainRecord>(switchableDomains.map((record) => [record.id, record])),
        [switchableDomainIdsKey],
    )
    const { addToRecents } = useRecents(userLevelPreferences, switchableDomainsById, RECENT_DOMAINS_LIMIT)

    const canOpenDomain = useCallback(
        (domainId: number) => switchableDomainsById.has(domainId),
        [switchableDomainsById],
    )

    // What the click handler needs changes on every render (the recents list is re-read each time), but the handler
    // itself must keep one identity so tables that memoize their columns on it are not rebuilt on every render.
    const latest = useRef({ switchableDomainsById, addToRecents })
    latest.current = { switchableDomainsById, addToRecents }

    // The dashboard is an organization-context page, so opening a domain is always a switch of context.
    const openInDomain = useCallback(
        async (domainId: number, path: string, event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault()
            if (event.metaKey || event.ctrlKey) {
                window.open(`${appState.platformHost}${path}`)
                return
            }
            const record = latest.current.switchableDomainsById.get(domainId)
            if (!record) return
            latest.current.addToRecents(domainId)
            await appService.setCurrentDomain(record)
            appService.route(path)
            appState.actionContext = AppActionContext.DOMAIN
        },
        [appState, appService],
    )

    return { canOpenDomain, openInDomain }
}
